import { useRef } from 'react';

import get from 'lodash/get';
import { useTranslation } from 'react-i18next';

import { LazySumsubWebSdk } from '../../../../../components/LazySumsubWebSdk';
import { analytics } from '../../../../../services/analytics';
import { useFlag } from '../../../../../services/launchDarkly';
import { useStores } from '../../../../../stores/useStores';
import type { PocViewFactory } from '../../../types';
import { PlaceholderView } from '../../shared/PlaceholderView';
import { PocViewConstructor } from '../../shared/PocViewConstructor/PocViewConstructor';
import { usePocProcess } from '../../shared/usePocProcess';
import { withCurrentSessionState } from '../../shared/withCurrentSessionState';

const FINISH_MARKERS = new Set<unknown>(['pending', 'onHold', 'completed']);

export const pocProcessViewFactory: PocViewFactory = {
  factoryName: 'pocView',
  factory: (_stepsMapping, _fallbackStep, { onSuccess }) =>
    withCurrentSessionState(
      function PocProcessView(props) {
        const { pocStore } = useStores();
        const { t } = useTranslation();
        const redesignDialogs = useFlag('ui_redesign_dialogs', false);

        const userInteractionHappened = useRef(false);

        const successfullyExit = () => {
          void analytics.sendEvent('automatic_poc', {
            step: 'automatic_poc_finished',
            event_action: 'impression',
            amount: props.pocState.extraData.redemptionAmount,
            triggered_by: 'completion',
          });

          pocStore.cleanupPocState();
          onSuccess();
        };

        usePocProcess(props.isCurrent);

        if (!props.isCurrent) {
          return <PlaceholderView />;
        }

        const handleMessage = (message: string, payload: unknown) => {
          if (message === 'idCheck.onStepInitiated') {
            userInteractionHappened.current = true;

            return;
          }

          if (!userInteractionHappened.current || message !== 'idCheck.actionStatus') {
            return;
          }

          const reviewStatus = get(payload, 'reviewStatus');

          if (FINISH_MARKERS.has(reviewStatus)) {
            return successfullyExit();
          }
        };

        return (
          <PocViewConstructor
            content={
              <LazySumsubWebSdk
                accessToken={props.pocState.coreData.accessToken}
                expirationHandler={() => pocStore.acquireAccessToken()}
                onMessage={handleMessage}
              />
            }
            newDesign={redesignDialogs}
            title={t('poc.cardVerification')}
          />
        );
      },
      'process',
      {
        getArgs: () => undefined,
        cleanupFn: (_, stores) => stores.pocStore.hangCurrentPocSession(),
      },
    ),
};
